import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Alert, Button, Form, ListGroup, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudArrowDown,
  faFolderOpen,
  faSearch,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { getDownloadURL, ref } from "firebase/storage";
import { addLog, getFilesIndex, storage } from "../../firebase";
import { useAuth } from "../../firebaseContext";

const MAX_FULL_RESULTS = 40;
const MAX_MINI_RESULTS = 6;

export default function FileIndexSearch({ mini = false }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [indexFiles, setIndexFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [downloadingFile, setDownloadingFile] = useState(null);

  const searchInputRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchFiles() {
      if (isMounted) {
        setLoading(true);
        setError(null);
      }

      try {
        const data = await getFilesIndex();
        const files = data.docs.map((doc) => doc.data());
        files.sort((a, b) =>
          (a.filename || "")
            .toLowerCase()
            .localeCompare((b.filename || "").toLowerCase())
        );

        if (isMounted) {
          setIndexFiles(files);
        }
      } catch (err) {
        console.error("Unable to load file index", err);
        if (isMounted) {
          setError(
            "We couldn't load the quick search index right now. Please refresh or try again shortly."
          );
          setIndexFiles([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchFiles();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!mini && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [mini, indexFiles.length]);

  const getReadableLocation = useCallback((location) => {
    if (!location) return "Uncategorised";

    const segments = location.split("/").filter(Boolean);
    if (!segments.length) return "Uncategorised";

    const first = segments[0].toLowerCase();
    const shouldDropFirst =
      /downloadable|viewable/.test(first) && segments.length > 1;
    const cleanedSegments = shouldDropFirst ? segments.slice(1) : segments;

    return cleanedSegments.join(" / ") || segments[segments.length - 1];
  }, []);

  const handleNavigate = useCallback(
    (folderPath) => {
      if (!folderPath) return;

      const folderArray = folderPath.split("/").filter(Boolean);
      const path = folderArray[folderArray.length - 1];

      if (!path) return;

      if (folderPath.toLowerCase().includes("viewable files")) {
        navigate(`/file/${encodeURIComponent(path)}`, {
          state: { fullPath: folderPath },
          replace: false,
        });
      } else {
        navigate(`/folder/${encodeURIComponent(path)}`, {
          state: { folder: folderPath },
          replace: false,
        });
      }
    },
    [navigate]
  );

  const filteredFiles = useMemo(() => {
    if (!indexFiles.length) return [];

    const query = searchValue.trim().toLowerCase();
    if (!query) {
      return indexFiles;
    }

    return indexFiles.filter((file) => {
      const name = (file.filename || "").toLowerCase();
      const location = (file.location || "").toLowerCase();
      return name.includes(query) || location.includes(query);
    });
  }, [indexFiles, searchValue]);

  const resultCount = filteredFiles.length;
  const hasQuery = Boolean(searchValue.trim());

  const visibleResults = useMemo(() => {
    const limit = mini ? MAX_MINI_RESULTS : MAX_FULL_RESULTS;
    return filteredFiles.slice(0, limit);
  }, [filteredFiles, mini]);

  const hasMoreResults = resultCount > visibleResults.length;

  const handleClearSearch = useCallback(() => {
    setSearchValue("");
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const handleDownload = useCallback(
    async (file) => {
      if (!file || !file.location || !file.filename) return;

      setDownloadingFile(file.filename);

      try {
        const fileRef = ref(storage, `${file.location}/${file.filename}`);
        const url = await getDownloadURL(fileRef);

        try {
          await addLog(
            currentUser?.email || "unknown",
            file.filename,
            "Downloaded"
          );
        } catch (logErr) {
          console.warn("Failed to record download in logs", logErr);
        }

        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = file.filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } catch (err) {
        console.error("Download failed", err);
      } finally {
        setDownloadingFile(null);
      }
    },
    [currentUser]
  );

  const summaryMessage = useMemo(() => {
    if (loading) {
      return "Searching the file index...";
    }

    if (!resultCount) {
      if (hasQuery) {
        return `No matches for "${searchValue.trim()}" just yet.`;
      }

      if (!indexFiles.length) {
        return "No files have been indexed yet.";
      }

      return "Nothing to show right now.";
    }

    if (hasQuery) {
      const label = resultCount === 1 ? "match" : "matches";
      return `Showing ${
        visibleResults.length
      } of ${resultCount} ${label} for "${searchValue.trim()}".`;
    }

    return `Showing ${visibleResults.length} of ${resultCount} indexed files.`;
  }, [
    hasQuery,
    indexFiles.length,
    loading,
    resultCount,
    searchValue,
    visibleResults.length,
  ]);

  const renderResultActions = useCallback(
    (file) => (
      <div className="quick-search-actions">
        <Button
          variant="outline-primary"
          size="sm"
          onClick={() => handleNavigate(file.location)}
          title="View folder"
        >
          <FontAwesomeIcon icon={faFolderOpen} className="me-2" />
          View
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => handleDownload(file)}
          disabled={downloadingFile === file.filename}
          title={`Download ${file.filename}`}
        >
          {downloadingFile === file.filename ? (
            <Spinner animation="border" size="sm" className="me-2" />
          ) : (
            <FontAwesomeIcon icon={faCloudArrowDown} className="me-2" />
          )}
          Download
        </Button>
      </div>
    ),
    [downloadingFile, handleDownload, handleNavigate]
  );

  if (mini) {
    return (
      <div className="quick-search-mini">
        <Form
          onSubmit={(event) => event.preventDefault()}
          className="quick-search-mini-form"
        >
          <div className="quick-search-mini-input">
            <FontAwesomeIcon icon={faSearch} />
            <Form.Control
              type="search"
              placeholder="Search the library"
              value={searchValue}
              onChange={(event) => setSearchValue(event.target.value)}
              ref={searchInputRef}
            />
            {searchValue && (
              <Button
                variant="link"
                className="quick-search-mini-clear"
                onClick={handleClearSearch}
              >
                <FontAwesomeIcon icon={faXmark} />
                <span className="visually-hidden">Clear search</span>
              </Button>
            )}
          </div>
        </Form>

        {loading ? (
          <div className="quick-search-mini-loading">
            <Spinner animation="border" role="status" />
          </div>
        ) : error ? (
          <Alert variant="danger" className="quick-search-mini-alert">
            {error}
          </Alert>
        ) : visibleResults.length ? (
          <ListGroup className="quick-search-mini-results">
            {visibleResults.map((file) => (
              <ListGroup.Item
                key={`${file.location}-${file.filename}`}
                className="quick-search-mini-item"
              >
                <div className="quick-search-mini-meta">
                  <div className="quick-search-mini-name" title={file.filename}>
                    {file.filename}
                  </div>
                  <div
                    className="quick-search-mini-location"
                    title={getReadableLocation(file.location)}
                  >
                    {getReadableLocation(file.location)}
                  </div>
                </div>
                <div className="quick-search-mini-actions">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleNavigate(file.location)}
                    title="View folder"
                  >
                    <FontAwesomeIcon icon={faFolderOpen} />
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleDownload(file)}
                    disabled={downloadingFile === file.filename}
                    title={`Download ${file.filename}`}
                  >
                    {downloadingFile === file.filename ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FontAwesomeIcon icon={faCloudArrowDown} />
                    )}
                  </Button>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        ) : (
          <Alert variant="light" className="quick-search-mini-empty">
            {hasQuery
              ? "No matches yet. Try a different keyword."
              : "Start typing to search the full library."}
          </Alert>
        )}

        {hasMoreResults && !loading && !error && (
          <div className="quick-search-mini-footnote text-muted">
            Showing the first {visibleResults.length} matches. Open the full
            index for more results.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="quick-search-panel">
      <Form
        onSubmit={(event) => event.preventDefault()}
        className="quick-search-panel-form"
      >
        <Form.Label className="quick-search-panel-label">
          Search the entire library
        </Form.Label>
        <div className="quick-search-panel-input">
          <FontAwesomeIcon icon={faSearch} />
          <Form.Control
            type="search"
            placeholder="Type a file or folder name"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            ref={searchInputRef}
          />
          {searchValue && (
            <Button
              type="button"
              variant="link"
              className="quick-search-panel-clear"
              onClick={handleClearSearch}
            >
              <FontAwesomeIcon icon={faXmark} />
              <span className="visually-hidden">Clear search</span>
            </Button>
          )}
        </div>
      </Form>

      {summaryMessage && (
        <div className="quick-search-panel-summary">{summaryMessage}</div>
      )}

      {error && (
        <Alert variant="danger" className="quick-search-panel-alert">
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="quick-search-panel-loading">
          <Spinner animation="border" role="status" />
        </div>
      ) : visibleResults.length ? (
        <ListGroup className="quick-search-panel-results">
          {visibleResults.map((file) => (
            <ListGroup.Item
              key={`${file.location}-${file.filename}`}
              className="quick-search-panel-item"
            >
              <div className="quick-search-panel-info">
                <div className="quick-search-panel-name" title={file.filename}>
                  {file.filename}
                </div>
                <div
                  className="quick-search-panel-location"
                  title={getReadableLocation(file.location)}
                >
                  {getReadableLocation(file.location)}
                </div>
              </div>
              {renderResultActions(file)}
            </ListGroup.Item>
          ))}
        </ListGroup>
      ) : (
        <Alert variant="light" className="quick-search-panel-empty">
          {hasQuery
            ? "No files match your search yet. Try another keyword."
            : "No files available to browse just yet."}
        </Alert>
      )}

      {hasMoreResults && !loading && !error && (
        <div className="quick-search-panel-footnote text-muted">
          More results are available. Narrow the search to refine the list.
        </div>
      )}
    </div>
  );
}
